import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { SkeletonPlaceholder } from '@carbon/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Button, ButtonSkeleton, Tooltip } from '@carbon/react';

import {
  ResourceGroup,
  Gateway,
  NetworkSegment,
  Cloud,
  Location,
  DeploymentEnvironment,
  Namespace,
  RemoteConnections,
  Error500Type,
  SkupperComputeProfile,
} from '../../models/master';
import {
  GatewaySubTypes,
  GatewayTypes,
  ResourceGroupTypes,
} from '../../lib/enums';
import dateUtils from '../../lib/dates';
import useAnalytics from '../../lib/useAnalytics';
import analyticsData from '../../lib/analyticsEventData';
import {
  RHSICustomComputeProfile,
  defaultNetworkSegmentObject,
} from '../../lib/constants';
import images from '../../images/images';

import {
  HealthStatusType,
  ProceduralStatusType,
} from '../../components/GatewayStatus/config';
import InlineNotification from '../../components/Notifications/Inline/Notification';
import RemoteConnectionsTable from './Remoteconnection/RemoteconnectionDetails/RemoteConnectionTable';
import GatewayDetailsVew from './GatewayDetailsView/GatewayDetailsView';
import Header from '../../components/Header/Header';
import { NotificationContext } from '../../components/Notifications/Context/NotificationProvider';
import DeleteGateway from './DeleteGateway/DeleteGateway';

import { getResourceGroups } from '../../controllers/resourceGroupApi';
import {
  getGateway,
  getGateways,
  getGatewayRemoteConnections,
  updateGateway,
  getRhsiComputeProfile,
} from '../../controllers/gateawayApis';
import { getNetworkSegments } from '../../controllers/networksegmentsApi';
import { getCloudLocation } from '../../controllers/locationApis';
import { getCloud } from '../../controllers/cloudApis';
import {
  getDeploymentEnv,
  getDeploymentEnvs,
  getDeploymentEnvSubtypes,
  getNamespaceDetails,
} from '../../controllers/deploymentEnv';

import './GatewayDetailsPage.scss';
import EditGateway from '../Gateways/EditGateway/EditGateway';
import SkupperConfiguration from './ConfigurationSkupperSettings/SkupperConfiguration';
import GatewayOnboardingStatus from '../../components/GatewayOnboardingStatus/GatewayOnboardingStatus';
import HealthStatus from '../../components/GatewayStatus/HealthStatus';
import ExternalClientAccessCard from './ExternalClientAccessCard/ExternalClientAccessCard';
import EditExternalClientAccess from './EditExternalClientAccess/EditExternalClientAccess';

const GatewayDetailsPage: React.FC = () => {
  const { t } = useTranslation('gatewayDetails');
  const [gwDetailsData, setGwDetailsData] = useState<Gateway | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('gatewayId');
  const [gwDataLoading, setGwDataLoading] = useState(false);
  const [showEditGateway, setShowEditGateway] = useState(false);
  const [resourceGroupsData, setResourceGroupsData] = useState<
    ResourceGroup[] | null
  >(null);
  const [existingGatewayNames, setExistingGatewayNames] = useState<string[]>(
    []
  );
  const [showGatewayStatusError, setShowGatewayStatusError] = useState(false);
  const [gatewaysList, setGatewaysList] = useState<Gateway[] | null>(null);
  const [openDeleteGatewayModal, setOpenDeleteGatewayModal] = useState(false);
  const [networkSegments, setNetworkSegments] = useState<
    NetworkSegment[] | null
  >(null);
  const [rhsiComputeProfiles, setRhsiComputeProfiles] = useState<
    SkupperComputeProfile[]
  >([]);
  const [cloud, setCloud] = useState<Cloud | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [cluster, setCluster] = useState<DeploymentEnvironment | null>(null);
  const [namespace, setNamespace] = useState<Namespace | null>(null);
  const [vpc, setVpc] = useState<DeploymentEnvironment | null>(null);
  const [namespaceLoading, setNamespaceLoading] = useState(false);
  const [deploymentEnvsList, setDeployomentEnvList] = useState<
    DeploymentEnvironment[] | null
  >(null);
  const [remoteConnectionsData, setRemoteConnectionsData] = useState<
    RemoteConnections[] | null
  >(null);
  const [error500, setError500] = useState<null | Error500Type>(null);

  const [headerData, setHeaderData] = useState<any>({});

  const [depEnvSubTypesList, setDepEnvSubTypesList] = useState<any>(null);
  const [editExternalClientAccess, setEditExternalClientAccess] =
    useState(false);
  const navigate = useNavigate();
  const notification = useContext(NotificationContext);
  const { trackButtonClicked, pageViewed } = useAnalytics();

  const breadcrumbs = [
    {
      url: '/',
      label: t('home'),
    },
    {
      url: '/gateways',
      label: t('gwServicesBreadcrumb'),
    },
  ];
  const wayptBreadcrumbs = [
    {
      url: '/',
      label: t('home'),
    },
    {
      url: '/gateways',
      label: t('wayptGwServicesBreadcrumb'),
    },
  ];

  const formatGwDetailsData = (data: Gateway) => {
    return {
      name: data?.name,
      infrasturctureGroup: getResourceGroupName(data?.resource_group_id),
      gatewaystatus: data?.procedural_status as ProceduralStatusType,
      lastUpdated: dateUtils.getUserFriendlyDate(data?.updated_at),
      labels: data?.labels as string[],
      description: data?.description ? data?.description : '—',
      cloud: getCloudData(),
      location: getLocationData(),
      healthStatus: data?.health_status as HealthStatusType,
      gwType: data?.subtype,
      intendedVersion: data?.intended_version,
      gatewaySubTypeName: t(data?.subtype),
      deployedIn: getDeployedIn(),
      deploymentType: data?.auto_deploy ? t('autodeploy') : t('manual'),
      terminate: data?.terminate,
      lifecycle_state: data?.lifecycle_state,
      procedure: data?.procedure,
      procedural_activity: data?.procedural_activity,
      unmanaged: data?.unmanaged,
      network_segment_id: data?.network_segment_id,
    };
  };

  const formatGwEntryPoints = (entrypoints: string) => {
    if (entrypoints === '') {
      return [];
    }
    const parsedObj = JSON.parse(entrypoints);

    const serviceKeys = Object.keys(parsedObj);

    return serviceKeys.map((key: string) => {
      const address = parsedObj[key].LoadBalancerIpAddress;
      return {
        service_name: key,
        ip: address,
        entry_points: parsedObj[key].Ports?.map(
          (port: string) => `${address}:${port}`
        ).join(', '),
      };
    });
  };

  const getResourceGroupName = (id: string | undefined) => {
    const resourceGroup =
      id && Array.isArray(resourceGroupsData)
        ? resourceGroupsData?.find(
            resourceGroup => resourceGroup.resource_id === id
          )
        : null;

    return resourceGroup ? resourceGroup.name : '—';
  };

  const getCloudData = () => {
    if (cloud) {
      return { resource_id: cloud.resource_id, name: cloud.name };
    }
    return null;
  };

  const getLocationData = () => {
    if (location) {
      return {
        resource_id: location.resource_id,
        cloud_id: location.cloud_id,
        name: location.name,
      };
    }
    return null;
  };

  const fetchDepEnvSubTypes = async () => {
    try {
      const envSubtypesList = await getDeploymentEnvSubtypes();
      setDepEnvSubTypesList(envSubtypesList);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchRhsiGwComputeProfiles = async () => {
    try {
      const computeProfiles = await getRhsiComputeProfile();
      Array.isArray(computeProfiles) && setRhsiComputeProfiles(computeProfiles);
    } catch (error) {
      console.log(error);
    }
  };

  const getNetworkSegmentData = () => {
    if (Array.isArray(networkSegments) && gwDetailsData) {
      if (
        gwDetailsData?.subtype === GatewaySubTypes.AXON_EDGE ||
        gwDetailsData?.subtype === GatewaySubTypes.AXON_WP
      ) {
        return {
          resource_id: defaultNetworkSegmentObject.resource_id,
          name: defaultNetworkSegmentObject.name,
        };
      } else {
        if (namespace) {
          const networkSegment = networkSegments.find(
            networkSegment =>
              networkSegment.resource_id === namespace.network_segment_id
          );

          if (networkSegment) {
            return {
              resource_id: networkSegment.resource_id,
              name: networkSegment.name,
            };
          }
        }
      }
    }

    return null;
  };

  const getDeployedIn = () => {
    if (namespace) {
      return {
        name: namespace.name,
        depEnvId: namespace.cluster_id,
        partitionId: namespace.resource_id,
      };
    }
    return null;
  };

  const getConnectedNamespace = () => {
    if (id && namespace) {
      return namespace.gateway_id === id ? namespace : null;
    }

    return null;
  };

  const fetchDeploymentEnv = async (id: string) => {
    try {
      const res = await getDeploymentEnv(id);
      return res;
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchGatewayData = async () => {
    //Gateway details data
    try {
      const gatewayData = await getGateway(id);
      setGwDetailsData(gatewayData);

      if (
        gatewayData.deployed_in_depl_env_id &&
        gatewayData.deployed_in_partition_id
      ) {
        const cluster = await fetchDeploymentEnv(
          gatewayData.deployed_in_depl_env_id
        );
        setCluster(cluster as DeploymentEnvironment);
        fetchNamespace(
          gatewayData.cloud_id,
          gatewayData.deployed_in_depl_env_id,
          gatewayData.deployed_in_partition_id
        );
      }

      if (gatewayData.deployed_in_vpc) {
        const vpc = await fetchDeploymentEnv(gatewayData.deployed_in_vpc);
        setVpc(vpc as DeploymentEnvironment);
      }

      if (gatewayData.cloud_id) {
        fetchCloud(gatewayData.cloud_id);

        if (gatewayData.location_id) {
          fetchLocation(gatewayData.cloud_id, gatewayData.location_id);
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status === 404) {
        navigate('/404');
      }
    }
  };

  const fetchNetworkSegments = async () => {
    try {
      const response = await getNetworkSegments();
      setNetworkSegments(response);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCloud = async (cloudId: string) => {
    try {
      const response = await getCloud(cloudId);
      setCloud(response);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchLocation = async (cloudId: string, locationId: string) => {
    try {
      const response = await getCloudLocation(cloudId, locationId);
      setLocation(response);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchNamespace = async (
    cloudId: string,
    deplId: string,
    partitionId: string
  ) => {
    setNamespaceLoading(true);
    try {
      const response = await getNamespaceDetails(cloudId, deplId, partitionId);
      setNamespace(response);
    } catch (error) {
      console.error(error);
    } finally {
      setNamespaceLoading(false);
    }
  };

  const fetchDeploymentEnvsList = async () => {
    try {
      const deploymentEnvsList = await getDeploymentEnvs();
      setDeployomentEnvList(deploymentEnvsList);
    } catch (error) {
      setDeployomentEnvList([]);
    }
  };

  const fetchRemoteConnections = async (gateways: Gateway[] | null) => {
    try {
      const remoteGwConnectionsResponse = await getGatewayRemoteConnections(id);
      const remoteConnections = remoteGwConnectionsResponse?.map(
        (item: any) => ({
          ...item,
          name:
            gateways && Array.isArray(gateways)
              ? gateways?.find(
                  resource => resource.resource_id === item?.remote_gw_id
                )?.name ?? ''
              : '',
        })
      );
      setRemoteConnectionsData(remoteConnections);
    } catch (error) {
      setRemoteConnectionsData([]);
    }
  };

  const fetchData = async () => {
    try {
      setGwDataLoading(true);

      const gateways = await getGateways();
      setGatewaysList(gateways);
      const existingGatewayNamesList = gateways.map(
        (gateway: Gateway) => gateway.name
      );
      setExistingGatewayNames(existingGatewayNamesList);

      await fetchGatewayData();

      fetchNetworkSegments();

      await fetchRemoteConnections(gateways);

      fetchDepEnvSubTypes();

      fetchDeploymentEnvsList();

      fetchRhsiGwComputeProfiles();

      const resourceGroupList = await getResourceGroups();
      setResourceGroupsData(
        resourceGroupList.resource_groups as ResourceGroup[]
      );
    } catch (error) {
      console.error(error);

      const err = error as AxiosError;
      if (err.response!?.status >= 500) {
        setError500(err.response!?.status.toString() as Error500Type);
      }
    } finally {
      setGwDataLoading(false);
    }
  };

  const updateExternalClientAccess = async (payload: {
    external_client_access: string;
    ip_address_pool: string;
  }) => {
    setEditExternalClientAccess(false);
    setGwDataLoading(true);
    try {
      await updateGateway(id, payload);

      notification.onTrigger('TOAST', {
        title: t('externalClientAccess.edit.successTitle'),
        subtitle: t('externalClientAccess.edit.successSubtitle', {
          name: gwDetailsData?.name,
        }),
      });

      await fetchGatewayData();
    } catch (error) {
      console.log('Error updating gw', error);
    } finally {
      setGwDataLoading(false);
    }
  };

  const getSubTitle = () => {
    return (
      <div className='sub-heading-section'>
        <div
          className={
            'header-icon-contaniner ' +
            (gwDetailsData?.unmanaged ? 'unmanaged' : 'managed')
          }
        >
          {gwDetailsData?.unmanaged
            ? images.unmanagedIcon()
            : images.managedIcon()}
          <div className='icon-text'>
            {gwDetailsData?.unmanaged ? t('unmanaged') : t('managed')}
          </div>
        </div>
        {!gwDetailsData?.unmanaged && (
          <HealthStatus
            status={gwDetailsData?.health_status as HealthStatusType}
            gatewayName={gwDetailsData?.name}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    pageViewed('Gateway Details');
  }, []);

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (gwDetailsData?.status === 'errored' && !showGatewayStatusError) {
      setShowGatewayStatusError(true);
    } else {
      if (showGatewayStatusError) {
        setShowGatewayStatusError(false);
      }
    }
  }, [gwDetailsData?.status]);

  const closeTearsheet = () => {
    setShowEditGateway(false);
    fetchData();
  };

  useEffect(() => {
    const dataSet = {
      gwDetails: gwDetailsData,
    };

    setHeaderData(dataSet);
  }, [gwDetailsData, gwDetailsData?.unmanaged]);

  useEffect(() => {
    let interval: any;

    if (
      gwDetailsData?.unmanaged &&
      (gwDetailsData?.procedural_activity === 'caching' ||
        gwDetailsData?.procedural_activity === 'waiting-for-closure')
    ) {
      interval = setInterval(() => {
        fetchGatewayData();
      }, 15000);
    } else {
      clearTimeout(interval);
    }

    return () => clearInterval(interval);
  }, [gwDetailsData]);

  const formatGwDataToEdit = (data: Gateway) => {
    const subType = data?.subtype;
    let cloud = getCloudData();
    let location = getLocationData();
    if (subType === GatewaySubTypes.RHSI_EDGE) {
      return {
        namespaceName: namespace ? namespace.name : '',
        clusterName: cluster ? cluster.name : '',
        cloudName: cloud?.name ? cloud?.name : '',
        locationName: location?.name ? location?.name : '',
        ingressEnabled: false,
        ...data,
      };
    }
  };

  const openEditEdgeGatewayModal = (gatewayId: string) => {
    if (id === gwDetailsData?.resource_id) {
      setShowEditGateway(true);
    }
  };

  const getGatewayNetworkSegment = () => {
    if (Array.isArray(networkSegments) && gwDetailsData) {
      return networkSegments.find(
        networkSegment =>
          networkSegment.resource_id === gwDetailsData.network_segment_id
      );
    }
  };

  const getDeleteButton = () => {
    const namespaces = getConnectedNamespace();

    // namespaceLoading flag is added to make sure that namespace are fetched before enabling the delete gateway button
    if (namespaceLoading) {
      return <ButtonSkeleton />;
    }

    if (namespaces) {
      return (
        <Tooltip
          className='delete-resource-button-container'
          align='right'
          label={t('deleteGateway.tooltipText') as string}
        >
          <button className='delete-resource-button disabled'>
            {t('deleteGateway.deleteGatewayBtn')}
          </button>
        </Tooltip>
      );
    }

    return (
      <div className='delete-resource-button-container'>
        <Button
          className='delete-resource-button'
          kind='danger--ghost'
          onClick={() => {
            trackButtonClicked(
              analyticsData['Gateway Details'].events.deleteGateway.props,
              analyticsData['Gateway Details'].events.deleteGateway.event
            );
            setOpenDeleteGatewayModal(true);
          }}
        >
          {t('deleteGateway.deleteGatewayBtn')}
        </Button>
      </div>
    );
  };

  return (
    <div className='gateway-details-container resource-details'>
      <Header
        title={gwDetailsData ? headerData : ''}
        breadcrumbs={
          gwDetailsData?.type === GatewayTypes.EDGE
            ? breadcrumbs
            : wayptBreadcrumbs
        }
        subTitle={getSubTitle()}
        loading={gwDataLoading}
      />
      <div className='page-content'>
        {gwDataLoading ? (
          <div className='tiles'>
            <SkeletonPlaceholder className='gateway-details-skeleton' />
            <SkeletonPlaceholder className='gateway-details-skeleton' />
          </div>
        ) : (
          <>
            {gwDetailsData?.unmanaged &&
              gwDetailsData?.procedure === 'onboard' &&
              gwDetailsData?.lifecycle_state === 'gw-deployed' && (
                <div className='banner'>
                  <GatewayOnboardingStatus
                    gateway={gwDetailsData}
                    networkSegment={getGatewayNetworkSegment()}
                    refreshData={() => {
                      fetchGatewayData();
                      fetchNetworkSegments();
                    }}
                  />
                </div>
              )}

            {showGatewayStatusError && (
              <InlineNotification
                kind={'error'}
                hideCloseButton={true}
                title={t('gatewayStatusError.title')}
              >
                <div className='gateway-status-error-notification'>
                  <div>
                    {t('gatewayStatusError.subtitle', {
                      gatewayName: gwDetailsData?.name,
                    })}
                  </div>
                  <div
                    className='link'
                    onChange={(e: any) => {
                      e.stopPropagation();
                      window.open(
                        window.location.origin +
                          process.env.PUBLIC_URL +
                          `/event?resource_name=${gwDetailsData?.name}`
                      );
                    }}
                  >
                    {t('gatewayStatusError.link')}
                  </div>
                </div>
              </InlineNotification>
            )}

            <div className='tiles'>
              <GatewayDetailsVew
                data={gwDetailsData ? formatGwDetailsData(gwDetailsData) : null}
                networkSegment={getGatewayNetworkSegment()}
                isEdge={gwDetailsData?.type === GatewayTypes.EDGE}
                isWyPt={gwDetailsData?.type !== GatewayTypes.EDGE}
                //disable editing of gateway until it become a managed gateway ie onboarding is completed.
                isEditable={!gwDetailsData?.unmanaged}
                openEditModal={() => {
                  trackButtonClicked(
                    analyticsData['Gateway Details'].events.editGateway.props,
                    analyticsData['Gateway Details'].events.editGateway.event
                  );
                  gwDetailsData?.resource_id &&
                    openEditEdgeGatewayModal(gwDetailsData?.resource_id);
                }}
              />

              <ExternalClientAccessCard
                access={gwDetailsData?.external_client_access ?? ''}
                serviceEntryPoints={formatGwEntryPoints(
                  gwDetailsData?.service_entrypoints ?? ''
                )}
                ipAddressPool={gwDetailsData?.ip_address_pool ?? ''}
                openEditTearsheet={() => setEditExternalClientAccess(true)}
              />
            </div>
          </>
        )}
        {showEditGateway && gwDetailsData && (
          <EditGateway
            deploymentEnvsList={deploymentEnvsList}
            depEnvSubTypesList={depEnvSubTypesList}
            resourceGroupsData={
              resourceGroupsData
                ? resourceGroupsData?.filter(
                    (group: any) =>
                      group.type === ResourceGroupTypes.INFRASTRUCTURE
                  )
                : null
            }
            gwDetailsData={gwDetailsData}
            gatewayDataToEdit={formatGwDataToEdit(gwDetailsData)}
            gatewayType={gwDetailsData?.type}
            gatewaySubtype={gwDetailsData?.subtype}
            gatewayNamesList={existingGatewayNames}
            open={showEditGateway}
            onClose={() => closeTearsheet()}
            gatewaysList={gatewaysList}
            onGatewayCreate={() => {
              setShowEditGateway(false);
              fetchData();
            }}
          />
        )}

        {gwDataLoading && (
          <SkeletonPlaceholder className='skupper-configuration-skeleton' />
        )}

        {gwDetailsData?.subtype === GatewaySubTypes.RHSI_EDGE &&
          !gwDataLoading && (
            <SkupperConfiguration
              skupperSiteConfig={
                gwDetailsData?.site_configuration
                  ? JSON.parse(gwDetailsData?.site_configuration)
                  : null
              }
              gatewaySizing={
                gwDetailsData?.intended_compute_profile ===
                RHSICustomComputeProfile.profile
                  ? RHSICustomComputeProfile
                  : rhsiComputeProfiles.find(
                      data =>
                        data?.profile ===
                        gwDetailsData?.intended_compute_profile
                    ) ?? null
              }
            />
          )}

        <RemoteConnectionsTable
          gatewayData={gwDetailsData}
          gatewayID={id}
          gatewaysList={gatewaysList}
          networkSegment={getNetworkSegmentData()}
        />
        {getDeleteButton()}
      </div>
      <DeleteGateway
        gatewayDetails={gwDetailsData}
        remoteConnections={remoteConnectionsData}
        deploymentEnvs={deploymentEnvsList}
        namespace={getConnectedNamespace()}
        open={openDeleteGatewayModal}
        cancelDeleteGatewayCallback={setOpenDeleteGatewayModal}
      />
      {editExternalClientAccess && (
        <EditExternalClientAccess
          data={{
            name: gwDetailsData?.name ?? '—',
            cluster: cluster,
            namespace: namespace?.name ?? '—',
            vpc: vpc?.name ?? '—',
            externalClientAccess: gwDetailsData?.external_client_access ?? '',
            ipAddressPool: gwDetailsData?.ip_address_pool ?? '',
            entryPoints: formatGwEntryPoints(
              gwDetailsData?.service_entrypoints ?? ''
            ),
          }}
          open={editExternalClientAccess}
          closeModal={() => setEditExternalClientAccess(false)}
          confirmSave={updateExternalClientAccess}
          vpcList={
            deploymentEnvsList?.filter(
              (env: DeploymentEnvironment) =>
                env.type === 'vpc' &&
                cluster?.cloud_id === env?.cloud_id &&
                cluster?.location_id === env?.location_id
            ) ?? []
          }
          deploymentEnvSubtypes={depEnvSubTypesList}
        />
      )}
    </div>
  );
};

export default GatewayDetailsPage;
